import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Search,
  ShieldAlert,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Database,
  Filter,
  Bug,
  ExternalLink,
  Loader2,
  X,
} from 'lucide-react';
import { IS_PRERENDER } from '@/lib/prerender';

const API_BASE = 'https://api.threat.live/api/cve_search.php';

// SOC-style severity badge classes
const severityConfig = {
  critical: { cls: 'soc-badge soc-badge--red', label: 'CRITICAL', order: 0 },
  high:     { cls: 'soc-badge soc-badge--neutral !text-orange-400 !border-orange-400/40 !bg-orange-400/10', label: 'HIGH', order: 1 },
  medium:   { cls: 'soc-badge soc-badge--amber', label: 'MEDIUM', order: 2 },
  low:      { cls: 'soc-badge soc-badge--neutral !text-cyan-400 !border-cyan-400/40 !bg-cyan-400/10', label: 'LOW', order: 3 },
  info:     { cls: 'soc-badge soc-badge--neutral', label: 'INFO', order: 4 },
};

const CVEPage = () => {
  const { language } = useLanguage();
  const [data, setData] = useState([]);
  const [overview, setOverview] = useState(null);
  const [filteredStats, setFilteredStats] = useState(null);
  const [meta, setMeta] = useState({ page: 1, limit: 30, total: 0, total_pages: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [kevFilter, setKevFilter] = useState('all');
  const [sortField, setSortField] = useState('modified_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const [expandedRow, setExpandedRow] = useState(null);

  const translations = {
    tr: {
      title: 'CVE & KEV Veritabanı',
      subtitle: 'Zafiyet arama ve keşif platformu',
      searchPlaceholder: 'CVE ID, açıklama veya anahtar kelime ara...',
      severity: 'Önem Derecesi',
      allSeverities: 'Tüm Dereceleri',
      kevOnly: 'Sadece KEV',
      allEntries: 'Tüm Kayıtlar',
      kevYes: 'KEV',
      totalCves: 'Toplam CVE',
      kevTotal: 'KEV Kayıtları',
      critical: 'Kritik',
      high: 'Yüksek',
      medium: 'Orta',
      low: 'Düşük',
      info: 'Bilgi',
      cveId: 'CVE ID',
      cvss: 'CVSS',
      published: 'Yayınlanma',
      modified: 'Güncellenme',
      description: 'Açıklama',
      noResults: 'Sonuç bulunamadı',
      loading: 'Yükleniyor...',
      page: 'Sayfa',
      of: '/',
      results: 'sonuç',
      search: 'Ara',
      clearFilters: 'Filtreleri Temizle',
      sortBy: 'Sıralama',
      newest: 'En Yeni',
      oldest: 'En Eski',
      highestCvss: 'En Yüksek CVSS',
      filtered: 'Filtrelenmiş',
    },
    en: {
      title: 'CVE & KEV Database',
      subtitle: 'Vulnerability search and discovery platform',
      searchPlaceholder: 'Search CVE ID, description, or keyword...',
      severity: 'Severity',
      allSeverities: 'All Severities',
      kevOnly: 'KEV Only',
      allEntries: 'All Entries',
      kevYes: 'KEV',
      totalCves: 'Total CVEs',
      kevTotal: 'KEV Entries',
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      info: 'Info',
      cveId: 'CVE ID',
      cvss: 'CVSS',
      published: 'Published',
      modified: 'Modified',
      description: 'Description',
      noResults: 'No results found',
      loading: 'Loading...',
      page: 'Page',
      of: 'of',
      results: 'results',
      search: 'Search',
      clearFilters: 'Clear Filters',
      sortBy: 'Sort By',
      newest: 'Newest',
      oldest: 'Oldest',
      highestCvss: 'Highest CVSS',
      filtered: 'Filtered',
    },
  };

  const txt = translations[language] || translations.en;

  const fetchCVEs = useCallback(async () => {
    if (IS_PRERENDER) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '30',
        sort: sortField,
        order: sortOrder,
      });

      if (searchTerm) params.set('q', searchTerm);
      if (severityFilter !== 'all') params.set('severity', severityFilter);
      if (kevFilter === 'kev') params.set('is_kev', '1');

      const response = await fetch(`${API_BASE}?${params}`);
      const json = await response.json();

      if (json.success) {
        setData(json.data || []);
        setMeta(json.meta || {});
        setOverview(json.overview || null);
        setFilteredStats(json.filtered_stats || null);
      }
    } catch (err) {
      console.error('Failed to fetch CVEs:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, severityFilter, kevFilter, sortField, sortOrder]);

  useEffect(() => {
    fetchCVEs();
  }, [fetchCVEs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchTerm(searchInput);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSeverityFilter('all');
    setKevFilter('all');
    setSortField('modified_date');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchTerm || severityFilter !== 'all' || kevFilter !== 'all';

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr.replace(' ', 'T') + 'Z');
    return d.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSeverityBadge = (severity) => {
    const config = severityConfig[severity] || severityConfig.info;
    return (
      <span className={config.cls}>
        {severity || 'info'}
      </span>
    );
  };

  const getCvssColor = (score) => {
    if (!score) return 'text-muted-foreground';
    const n = parseFloat(score);
    if (n >= 9.0) return 'text-red-500 font-bold';
    if (n >= 7.0) return 'text-orange-400 font-semibold';
    if (n >= 4.0) return 'text-amber-400';
    return 'text-cyan-400';
  };

  const displayStats = hasActiveFilters ? filteredStats : overview;

  const pageNumbers = useMemo(() => {
    const total = meta.total_pages || 1;
    const current = meta.page || 1;
    const pages = [];
    const delta = 2;

    for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
      pages.push(i);
    }
    if (pages[0] > 1) {
      if (pages[0] > 2) pages.unshift('...');
      pages.unshift(1);
    }
    if (pages[pages.length - 1] < total) {
      if (pages[pages.length - 1] < total - 1) pages.push('...');
      pages.push(total);
    }
    return pages;
  }, [meta.total_pages, meta.page]);

  // Compact overview panel card
  const OverviewCard = ({ label, value, tone = 'text-foreground', highlight = false }) => (
    <div className={`border border-border bg-card ${highlight ? 'border-primary/40' : ''}`}>
      <div className={`px-3 py-1.5 border-b border-border ${highlight ? 'bg-primary/10' : 'bg-muted/30'}`}>
        <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </span>
      </div>
      <div className="px-3 py-2">
        <p className={`font-mono tabular text-xl font-bold ${tone}`}>
          {Number(value || 0).toLocaleString()}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="cve-page">
      <SEO page="cve" />
      <Header />

      {/* Hero */}
      <section className="relative py-10 overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-bg mask-radial-fade opacity-70 pointer-events-none" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[260px] bg-red-500/8 blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 relative z-10">
          {/* Section meta */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary">
              // VULN.INTEL · R-03
            </span>
            <span className="flex-1 h-px bg-border" />
            <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
              <span className="status-dot" />
              SYNCED
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 border border-red-500/40 bg-red-500/10 sharp">
                  <ShieldAlert className="h-6 w-6 text-red-400" strokeWidth={2.25} />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                  {txt.title}
                </h1>
              </div>
              <p className="text-muted-foreground ml-[52px] md:ml-[60px]">{txt.subtitle}</p>
            </div>

            {overview && (
              <div className="flex items-center gap-2">
                <span className="soc-badge soc-badge--neutral">
                  <Database className="h-3 w-3" />
                  <span className="tabular">{Number(overview.total_cves).toLocaleString()}</span> CVE
                </span>
                <span className="soc-badge soc-badge--red">
                  <ShieldCheck className="h-3 w-3" />
                  <span className="tabular">{Number(overview.kev_total).toLocaleString()}</span> KEV
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      <main className="container mx-auto px-4 py-6">
        {/* Overview Cards */}
        {displayStats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6" data-testid="cve-stats-cards">
            <OverviewCard
              label={hasActiveFilters ? txt.filtered : txt.totalCves}
              value={displayStats.total || displayStats.total_cves || 0}
              tone="text-foreground"
              highlight
            />
            <OverviewCard label="KEV" value={displayStats.kev_total} tone="text-red-400" />
            <OverviewCard label={txt.critical} value={displayStats.severity_counts?.critical} tone="text-red-500" />
            <OverviewCard label={txt.high} value={displayStats.severity_counts?.high} tone="text-orange-400" />
            <OverviewCard label={txt.medium} value={displayStats.severity_counts?.medium} tone="text-amber-400" />
            <OverviewCard label={txt.low} value={displayStats.severity_counts?.low} tone="text-cyan-400" />
            <OverviewCard label={txt.info} value={displayStats.severity_counts?.info} tone="text-muted-foreground" />
          </div>
        )}

        {/* Search & Filters panel */}
        <div className="border border-border bg-card mb-6" data-testid="cve-filters">
          <div className="flex items-center justify-between px-5 py-2 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Search className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
              <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground">
                {language === 'tr' ? 'SORGU' : 'QUERY'}
              </span>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              // FILTER · SORT
            </span>
          </div>
          <div className="p-4">
            <div className="flex flex-col md:flex-row gap-3">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={txt.searchPlaceholder}
                    className="pl-10 sharp font-mono text-xs"
                    data-testid="cve-search-input"
                  />
                </div>
                <Button
                  type="submit"
                  size="default"
                  className="sharp font-mono text-xs uppercase tracking-wider bg-primary text-primary-foreground hover:bg-primary/90 border border-primary"
                  data-testid="cve-search-btn"
                >
                  <Search className="h-4 w-4 mr-1" />
                  {txt.search}
                </Button>
              </form>

              <div className="flex gap-2 flex-wrap">
                <Select value={severityFilter} onValueChange={(v) => { setSeverityFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger className="w-[140px] sharp font-mono text-xs" data-testid="cve-severity-filter">
                    <SelectValue placeholder={txt.severity} />
                  </SelectTrigger>
                  <SelectContent className="sharp font-mono">
                    <SelectItem value="all">{txt.allSeverities}</SelectItem>
                    <SelectItem value="critical">{txt.critical}</SelectItem>
                    <SelectItem value="high">{txt.high}</SelectItem>
                    <SelectItem value="medium">{txt.medium}</SelectItem>
                    <SelectItem value="low">{txt.low}</SelectItem>
                    <SelectItem value="info">{txt.info}</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={kevFilter} onValueChange={(v) => { setKevFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger className="w-[130px] sharp font-mono text-xs" data-testid="cve-kev-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="sharp font-mono">
                    <SelectItem value="all">{txt.allEntries}</SelectItem>
                    <SelectItem value="kev">{txt.kevOnly}</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={`${sortField}_${sortOrder}`} onValueChange={(v) => {
                  const [field, order] = v.split('_');
                  setSortField(field);
                  setSortOrder(order);
                  setCurrentPage(1);
                }}>
                  <SelectTrigger className="w-[150px] sharp font-mono text-xs" data-testid="cve-sort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="sharp font-mono">
                    <SelectItem value="modified_date_desc">{txt.newest}</SelectItem>
                    <SelectItem value="modified_date_asc">{txt.oldest}</SelectItem>
                    <SelectItem value="cvss_v3_score_desc">{txt.highestCvss}</SelectItem>
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleClearFilters}
                    className="sharp text-muted-foreground hover:text-foreground"
                    data-testid="cve-clear-filters"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Results info */}
        <div className="flex items-center justify-between mb-3 text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
          <p>
            <span className="text-foreground tabular">{meta.total?.toLocaleString()}</span> {txt.results}
            {hasActiveFilters && (
              <span className="ml-2 soc-badge soc-badge--neutral !py-[2px] !px-1.5 !text-[9px]">
                <Filter className="h-2.5 w-2.5" />
                {txt.filtered}
              </span>
            )}
          </p>
          <p>
            {txt.page} <span className="text-foreground tabular">{meta.page}</span> {txt.of} <span className="text-foreground tabular">{meta.total_pages?.toLocaleString()}</span>
          </p>
        </div>

        {/* CVE Table panel */}
        <div className="border border-border bg-card overflow-hidden mb-6" data-testid="cve-table">
          <div className="flex items-center justify-between px-5 py-2 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Bug className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
              <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground">
                {language === 'tr' ? 'ZAFİYET KAYITLARI' : 'VULNERABILITY RECORDS'}
              </span>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              NVD / CISA KEV SYNC
            </span>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border bg-muted/10">
                <TableHead className="w-[170px] font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{txt.cveId}</TableHead>
                <TableHead className="w-[100px] font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{txt.severity}</TableHead>
                <TableHead className="w-[70px] font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{txt.cvss}</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{txt.description}</TableHead>
                <TableHead className="w-[110px] font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground hidden lg:table-cell">{txt.published}</TableHead>
                <TableHead className="w-[110px] font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground hidden lg:table-cell">{txt.modified}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-20">
                    <div className="flex items-center justify-center gap-2 text-muted-foreground font-mono text-xs uppercase tracking-wider">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {txt.loading}
                    </div>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-20">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground font-mono text-xs uppercase tracking-wider">
                      <Bug className="h-8 w-8" />
                      <p>{txt.noResults}</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item) => (
                  <React.Fragment key={item.cve_id}>
                    <TableRow
                      className="hover:bg-muted/40 cursor-pointer transition-colors border-b border-border/60"
                      onClick={() => setExpandedRow(expandedRow === item.cve_id ? null : item.cve_id)}
                      data-testid={`cve-row-${item.cve_id}`}
                    >
                      <TableCell className="font-mono text-xs font-semibold">
                        <div className="flex items-center gap-2">
                          <a
                            href={`https://nvd.nist.gov/vuln/detail/${item.cve_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-foreground hover:text-primary transition-colors flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {item.cve_id}
                            <ExternalLink className="h-3 w-3 opacity-50" />
                          </a>
                          {item.is_kev === 1 && (
                            <span className="soc-badge soc-badge--red !py-[2px] !px-1.5 !text-[9px]">KEV</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getSeverityBadge(item.severity)}</TableCell>
                      <TableCell>
                        <span className={`font-mono text-sm tabular ${getCvssColor(item.cvss_v3_score || item.cvss_v2_score)}`}>
                          {item.cvss_v3_score || item.cvss_v2_score || '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                          {item.description}
                        </p>
                      </TableCell>
                      <TableCell className="text-[11px] font-mono text-muted-foreground hidden lg:table-cell whitespace-nowrap">
                        {formatDate(item.published_date)}
                      </TableCell>
                      <TableCell className="text-[11px] font-mono text-muted-foreground hidden lg:table-cell whitespace-nowrap">
                        {formatDate(item.modified_date)}
                      </TableCell>
                    </TableRow>

                    {expandedRow === item.cve_id && (
                      <TableRow className="bg-muted/20 border-b border-border/60">
                        <TableCell colSpan={6} className="p-4">
                          <div className="space-y-3">
                            <div className="border-l-2 border-primary pl-3">
                              <p className="text-sm leading-relaxed text-foreground/90">{item.description}</p>
                            </div>
                            <div className="flex flex-wrap gap-3 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                              {item.cvss_v3_score && (
                                <span>CVSS v3: <span className={`${getCvssColor(item.cvss_v3_score)} tabular`}>{item.cvss_v3_score}</span></span>
                              )}
                              {item.cvss_v2_score && (
                                <span>CVSS v2: <span className={`${getCvssColor(item.cvss_v2_score)} tabular`}>{item.cvss_v2_score}</span></span>
                              )}
                              {item.cvss_v3_vector && (
                                <span className="font-mono text-[10px] bg-muted px-2 py-0.5 sharp border border-border">{item.cvss_v3_vector}</span>
                              )}
                              <span>{txt.published}: <span className="text-foreground">{formatDate(item.published_date)}</span></span>
                              <span>{txt.modified}: <span className="text-foreground">{formatDate(item.modified_date)}</span></span>
                              {item.is_kev === 1 && (
                                <span className="soc-badge soc-badge--red">KNOWN EXPLOITED</span>
                              )}
                            </div>
                            <a
                              href={`https://nvd.nist.gov/vuln/detail/${item.cve_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-primary hover:text-primary/80 transition-colors"
                            >
                              → NVD DETAIL <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {meta.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1 mb-8" data-testid="cve-pagination">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 sharp"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              data-testid="cve-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {pageNumbers.map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground text-sm font-mono">...</span>
              ) : (
                <Button
                  key={p}
                  variant={p === currentPage ? 'default' : 'outline'}
                  size="sm"
                  className={`h-8 w-8 p-0 sharp font-mono text-xs tabular ${p === currentPage ? 'bg-primary text-primary-foreground border-primary' : ''}`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </Button>
              )
            )}

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 sharp"
              disabled={currentPage >= meta.total_pages}
              onClick={() => setCurrentPage(currentPage + 1)}
              data-testid="cve-next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CVEPage;
